import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { HandCoins, RefreshCw } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { SignalBacktestPanel } from "../components/SignalBacktestPanel";
import { useAuth } from "../App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const usd = (v) =>
  v == null ? "—" : v >= 1e9 ? `$${(v / 1e9).toFixed(1)}B` : `$${(v / 1e6).toFixed(1)}M`;
const pct = (v, dp = 1) => (v == null ? "—" : `${(v * 100).toFixed(dp)}%`);

const DriftBadge = ({ r }) => {
  if (r.drift == null)
    return <span className="text-xs text-muted-foreground font-mono">—</span>;
  const cls = r.drift_ok
    ? "text-emerald-300 border-emerald-500/40"
    : r.drift > 1.1
      ? "text-rose-400 border-rose-600/50"
      : "text-amber-300 border-amber-500/40";
  const lbl = r.drift_ok ? "stable" : r.drift > 1.1 ? "leak?" : "rallied";
  return (
    <Badge variant="outline" className={`text-[10px] font-mono ${cls}`}
           title="entry yield ÷ yield at quarter-end">
      {r.drift.toFixed(2)} {lbl}
    </Badge>
  );
};

const SignalRow = ({ r }) => (
  <tr className="border-b border-border/40 hover:bg-muted/30"
      data-testid={`div-row-${r.symbol}-${r.accepted_date}`}>
    <td className="py-2 px-2 font-bold">{r.symbol}</td>
    <td className="py-2 px-2 text-right font-mono text-teal-300">{pct(r.yld)}</td>
    <td className="py-2 px-2"><DriftBadge r={r} /></td>
    <td className="py-2 px-2">
      {r.book && (
        <Badge variant="outline" className="text-[10px] bg-teal-500/15 text-teal-300 border-teal-500/50">
          BOOK
        </Badge>
      )}
      {!r.book && r.ia_ok && (
        <Badge variant="outline" className="text-[10px] text-sky-300 border-sky-500/40">IA OK</Badge>
      )}
      {!r.ia_ok && (
        <Badge variant="outline" className="text-[10px] text-muted-foreground border-border">
          ia {r.ia_skew ?? "—"}
        </Badge>
      )}
      {r.trap_flag && (
        <Badge variant="outline" className="ml-1 text-[10px] text-rose-400 border-rose-600/50">
          TRAP ≥10%
        </Badge>
      )}
      {r.n_conv >= 2 && (
        <Badge variant="outline" className="ml-1 text-[10px] text-amber-300 border-amber-500/40">
          ×{r.n_conv}
        </Badge>
      )}
    </td>
    <td className="py-2 px-2 text-right font-mono text-xs">{r.ia_skew ?? "—"}</td>
    <td className="py-2 px-2 text-xs">{r.fund}</td>
    <td className="py-2 px-2 font-mono text-xs text-muted-foreground">{r.accepted_date}</td>
    <td className="py-2 px-2 text-right font-mono text-xs">{usd(r.delta_value)}</td>
    <td className="py-2 px-2 text-right font-mono text-xs">
      {r.mcap_b != null ? `$${r.mcap_b}B` : "—"}
    </td>
    <td className={`py-2 px-2 text-right font-mono ${
      (r.excess_since || 0) >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
      {pct(r.excess_since)}
    </td>
    <td className="py-2 px-2 text-xs text-muted-foreground">{r.sector || "—"}</td>
  </tr>
);

const SignalTable = ({ rows, testid }) => (
  <table className="w-full text-sm" data-testid={testid}>
    <thead className="border-b border-border text-muted-foreground text-xs">
      <tr>
        <th className="py-2 px-2 text-left">Symbol</th>
        <th className="py-2 px-2 text-right">Yield</th>
        <th className="py-2 px-2 text-left">Drift</th>
        <th className="py-2 px-2 text-left">Signal</th>
        <th className="py-2 px-2 text-right">ia_skew</th>
        <th className="py-2 px-2 text-left">Fund</th>
        <th className="py-2 px-2 text-left">Disclosed</th>
        <th className="py-2 px-2 text-right">Δ value</th>
        <th className="py-2 px-2 text-right">Mcap</th>
        <th className="py-2 px-2 text-right">vs SCHD since</th>
        <th className="py-2 px-2 text-left">Sector</th>
      </tr>
    </thead>
    <tbody>{rows.map((r, i) => <SignalRow key={`${r.symbol}-${i}`} r={r} />)}</tbody>
  </table>
);

export default function DividendAlphaPage() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [bookOnly, setBookOnly] = useState(true);

  const load = useCallback(async () => {
    const res = await fetch(`${API}/signals/dividend-alpha`, {
      headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) setData(await res.json());
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/signals/dividend-alpha/refresh`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` } });
      const body = await res.json();
      if (!res.ok) throw new Error(body.detail || "refresh failed");
      toast.info("Refresh started — backtest + signals rebuild in ~3 min");
      setTimeout(load, 180000);
    } catch (e) {
      toast.error(String(e.message || e));
    } finally { setBusy(false); }
  };

  const rows = (data?.rows || []).filter((r) => (bookOnly ? r.book : true));
  const active = rows.filter((r) => r.active);
  const older = rows.filter((r) => !r.active);

  return (
    <div className="p-6 space-y-6" data-testid="dividend-alpha-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <HandCoins className="w-6 h-6 text-teal-300" /> Emergent Dividend
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
            Income-specialist cohort signal — funds graded (walk-forward EB, zero
            look-ahead) on all their ≥2%-yield buys; the book takes their NEW
            positions in ≥5% trailing-yield, ≥$2B names whose holder base has
            avg ia_skew ≥ median. Validated Oct-2026: +18.7%/yr, Sharpe 1.01,
            +47% cum vs SCHD; OOS Sharpe 1.36. ≥10% trailing yield is the
            dividend-trap zone (−47% vs SCHD) — flagged, never bought.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border border-border overflow-hidden text-xs">
            <button onClick={() => setBookOnly(true)} data-testid="div-book-filter"
                    className={`px-2.5 py-1.5 transition-colors ${
                      bookOnly ? "bg-secondary text-foreground font-semibold"
                               : "text-muted-foreground hover:text-foreground"}`}>
              Book
            </button>
            <button onClick={() => setBookOnly(false)} data-testid="div-watchlist-filter"
                    className={`px-2.5 py-1.5 transition-colors ${
                      !bookOnly ? "bg-secondary text-foreground font-semibold"
                                : "text-muted-foreground hover:text-foreground"}`}>
              Full watchlist (≥4%)
            </button>
          </div>
          <Button variant="outline" size="sm" onClick={refresh} disabled={busy}
                  data-testid="div-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${busy ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {!data ? (
        <Card><CardContent className="py-10 text-center text-muted-foreground">
          Not computed yet — hit Refresh to build the signal (takes ~3 min).
        </CardContent></Card>
      ) : (
        <>
          <div className="text-xs text-muted-foreground" data-testid="div-meta">
            {data.n_signals} signals · {data.n_book} in book ·
            ia median {data.ia_median} · cohort graded {data.grade_date} ·
            computed {data.computed_at?.slice(0, 16).replace("T", " ")} UTC
          </div>
          <SignalBacktestPanel slug="dividend-alpha" lineColor="#2dd4bf"
                               accent="text-teal-300" benchLabel="SCHD" />
          <Card data-testid="div-active-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">
                Active signals <span className="text-muted-foreground font-normal">
                (disclosed in last 90 days — inside the 63-session hold window)</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              {active.length ? <SignalTable rows={active} testid="div-active-table" />
                : <p className="text-sm text-muted-foreground py-4">No active signals.</p>}
            </CardContent>
          </Card>
          <Card data-testid="div-history-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">
                Recent history <span className="text-muted-foreground font-normal">
                (window expired — outcome tracking vs SCHD)</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              {older.length ? <SignalTable rows={older} testid="div-history-table" />
                : <p className="text-sm text-muted-foreground py-4">—</p>}
            </CardContent>
          </Card>
          <Card data-testid="div-cohort-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">
                Income-specialist cohort <span className="text-muted-foreground font-normal">
                (top EB quintile at {data.grade_date}, re-graded quarterly)</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border text-muted-foreground text-xs">
                  <tr>
                    <th className="py-2 px-2 text-left">Fund</th>
                    <th className="py-2 px-2 text-right"># div buys (train)</th>
                    <th className="py-2 px-2 text-right">EB grade</th>
                    <th className="py-2 px-2 text-right">Avg fwd-63d excess</th>
                    <th className="py-2 px-2 text-right">Hit rate</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.cohort || []).map((f) => (
                    <tr key={f.cik} className="border-b border-border/40"
                        data-testid={`div-cohort-${f.cik}`}>
                      <td className="py-1.5 px-2">{f.name}</td>
                      <td className="py-1.5 px-2 text-right font-mono">{f.n_buys}</td>
                      <td className="py-1.5 px-2 text-right font-mono text-sky-300">
                        {pct(f.eb_grade)}
                      </td>
                      <td className="py-1.5 px-2 text-right font-mono text-emerald-300">
                        {pct(f.avg_fwd63_excess)}
                      </td>
                      <td className="py-1.5 px-2 text-right font-mono">{pct(f.hit_rate, 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
